// Map free-text "Known Case Of" strings + diagnoses to the 26-code diet
// taxonomy. The mapping is intentionally forgiving — clinical shorthand and
// long-form both hit the same code. Anything unknown is silently ignored.
//
// Used by AdvancedDietChart to pre-select condition chips from the patient's
// visit data so the doctor barely has to click.

const RULES = [
  // Diabetes
  [/type\s*2\s*(dm|diabetes)|t2\s*d\s*m|niddm/i, "T2DM"],
  [/type\s*1\s*(dm|diabetes)|t1\s*d\s*m|iddm/i, "T1DM"],
  [/gdm|gestational\s*diabetes/i, "T2DM"],  // GDM → still T2DM class dietary
  [/diabetes\b/i, "T2DM"],
  // Kidney
  [/dialysis|hemodialysis|haemodialysis|peritoneal\s*dialysis|maintenance\s*hd|mhd\b/i, "DIALYSIS"],
  [/ckd|chronic\s*kidney|renal\s*failure|nephropathy/i, "CKD"],
  // Cardiac
  [/heart\s*failure|hfref|hfpef|cardiomyopathy|chf\b/i, "HF"],
  [/hypertension|htn\b|high\s*bp/i, "HTN"],
  // Liver
  [/masld|nafld|mash|nash|fatty\s*liver/i, "MASLD"],
  [/cirrhosis/i, "CIRRHOSIS"],
  [/hepatic\s*failure|liver\s*failure|encephalopathy/i, "HEPATIC_FAILURE"],
  // GI
  [/crohn|ulcerative\s*colitis|inflammatory\s*bowel|\bibd\b/i, "IBD"],
  [/\bibs\b|irritable\s*bowel/i, "IBS"],
  [/pancreatitis/i, "PANCREATITIS"],
  // Endocrine / metabolic
  [/hypothyroid|hashimoto/i, "HYPOTHYROID"],
  [/\bpcos\b|polycystic/i, "PCOS"],
  [/obesity|morbid\s*obese|bmi\s*[3-9]\d/i, "OBESITY"],
  // Rheumatology
  [/\bgout\b|hyperuricemia|hyperuricaemia/i, "GOUT"],
  // Special populations
  [/pregnan/i, "PREGNANCY"],
  [/lactat|breast[- ]?feed/i, "LACTATION"],
  [/cancer|oncology|malignancy|carcinoma/i, "ONCOLOGY"],
  [/geriatric|elderly|senile/i, "GERIATRIC"],
  [/paediatric|pediatric|\bchild\b/i, "PAEDIATRIC"],
  [/anaemia|anemia|iron[- ]deficiency/i, "ANAEMIA"],
  // Post-surgical
  [/dumping|post[- ]bariatric|gastric\s*bypass|sleeve\s*gastrec/i, "DUMPING"],
  // Allergies
  [/nut\s*allergy|peanut\s*allergy/i, "ALLERGY_NUTS"],
  [/dairy\s*allergy|milk\s*allergy|lactose/i, "ALLERGY_DAIRY"],
  [/coeliac|celiac|gluten/i, "ALLERGY_GLUTEN"],
  // Pulmonary
  [/\bcopd\b|chronic\s*obstructive/i, "COPD"],
  [/asthma|bronchospasm/i, "ASTHMA"],
  [/\btuberculosis|\btb\b|koch/i, "TB"],
  [/obstructive\s*sleep\s*apnoea|sleep\s*apnea|\bosa\b/i, "OSA"],
  [/interstitial\s*lung|ild\b|pulmonary\s*fibrosis/i, "ILD"],
  [/cystic\s*fibrosis|\bcf\b(?!\s*\d)/i, "CF"],
  // Additional liver
  [/hepatitis|\bhcv\b|\bhbv\b|autoimmune\s*hepatitis/i, "HEPATITIS"],
  [/cholestasis|primary\s*biliary|pbc\b|psc\b/i, "CHOLESTASIS"],
  // Cardio
  [/coronary\s*artery|post[- ]?mi\b|angina|ischemic\s*heart|ihd\b/i, "CAD"],
  [/dyslipid|hyperlipid|high\s*cholesterol/i, "DYSLIPIDEMIA"],
  [/metabolic\s*syndrome|syndrome\s*x/i, "METABOLIC_SYN"],
  [/stroke|cva\b|tia\b/i, "STROKE"],
  // Renal extra
  [/nephrotic/i, "NEPHROTIC"],
  [/kidney\s*stone|renal\s*calculi|nephrolithiasis|urolithiasis/i, "KIDNEY_STONE"],
  // GI extra
  [/gerd|reflux/i, "GERD"],
  [/diverticul/i, "DIVERTICULOSIS"],
  [/constipation/i, "CONSTIPATION"],
  // Neurology
  [/epilepsy|seizure\s*disorder/i, "EPILEPSY"],
  [/migraine/i, "MIGRAINE"],
  [/psoriasis/i, "PSORIASIS"],
];

// Extract CKD stage from a text blob if present, e.g. "CKD Stage 4" → G4.
function ckdStageFrom(text) {
  const m = String(text || "").match(/ckd\s*(?:stage|st|g)?\s*([1-5])(?:a|b)?/i);
  if (m) return `G${m[1]}`;
  return undefined;
}

// Extract eGFR if present, e.g. "eGFR 25", "eGFR: 25.4 mL/min".
function egfrFrom(text) {
  const m = String(text || "").match(/egfr[:\s]*([0-9]+(?:\.[0-9]+)?)/i);
  if (m) return parseFloat(m[1]);
  return undefined;
}

// Extract NYHA class, e.g. "HF NYHA III".
function nyhaFrom(text) {
  const m = String(text || "").match(/nyha[:\s]*(i{1,3}v?|1|2|3|4)/i);
  if (!m) return undefined;
  const t = m[1].toUpperCase();
  return ({ I: 1, II: 2, III: 3, IV: 4 }[t] || parseInt(t, 10));
}

export function autoDetectConditions({ known = [], diagnosisPrimary = "", diagnosisSecondary = "", medicines = [] } = {}) {
  const all = [
    ...(Array.isArray(known) ? known : []),
    diagnosisPrimary || "",
    diagnosisSecondary || "",
  ];
  const combinedText = all.join(" · ");

  const codes = new Map();  // code → detail
  for (const text of all) {
    if (!text) continue;
    for (const [rx, code] of RULES) {
      if (rx.test(text)) {
        if (!codes.has(code)) codes.set(code, {});
      }
    }
  }
  // Medicine-driven inferences — a GLP-1 or SGLT2 or insulin script implies T2DM.
  const medBrands = (medicines || []).map((m) => `${m?.generic_name || ""} ${m?.brand_name || ""}`).join(" ");
  const glp1 = /semaglutide|liraglutide|dulaglutide|tirzepatide|ozempic|wegovy|rybelsus|trulicity|mounjaro/i.exec(medBrands);
  const sglt2 = /empagliflozin|dapagliflozin|canagliflozin|jardiance|forxiga|oxra/i.exec(medBrands);
  const ins = /insulin|glargine|degludec|lispro|aspart|humalog|novorapid|lantus|tresiba|toujeo/i.exec(medBrands);
  const levo = /levothyroxine|thyroxine|eltroxin|thyronorm/i.exec(medBrands);
  if (glp1 || sglt2 || ins) {
    if (!codes.has("T2DM")) codes.set("T2DM", {});
    const d = codes.get("T2DM");
    if (glp1) d.on_glp1 = glp1[0];
    if (sglt2) d.on_sglt2 = sglt2[0];
    if (ins) d.on_insulin = true;
  }
  if (levo && !codes.has("HYPOTHYROID")) codes.set("HYPOTHYROID", { on_levothyroxine: true });

  // Post-processing: enrich CKD with stage/eGFR.
  if (codes.has("CKD")) {
    const d = codes.get("CKD");
    const stage = ckdStageFrom(combinedText);
    const eg = egfrFrom(combinedText);
    if (stage) d.stage = stage;
    if (eg) d.egfr = eg;
  }
  // NYHA on HF.
  if (codes.has("HF")) {
    const d = codes.get("HF");
    const nyha = nyhaFrom(combinedText);
    if (nyha) d.nyha = nyha;
    if (/hfref/i.test(combinedText)) d.type = "HFrEF";
    else if (/hfpef/i.test(combinedText)) d.type = "HFpEF";
  }

  return Array.from(codes.entries()).map(([code, detail]) => ({ code, detail }));
}
